import * as React from 'react';
import { CalendarOutlined } from '@ant-design/icons';
import {
	Button, Col, Row, Tabs,
} from 'antd';
import DateRangeSelector, {
	DateRangeSelectorProps,
} from '../forms/common/generics/daterangeselector';
import { disableFutureDates } from '@copilot/common/utils';
import styles from './dateRange.module.less';
import { getStartOfDay } from '@copilot/common/pages/campaignDashboard/summaryV2/dataFilterUtils';
import moment from 'moment';

export enum DateRangeFilterEnum {
	AllTime = 0,
	Last90 = 1,
	Last60 = 2,
	Last30 = 3,
	Last14 = 4,
	Last7 = 5,
	Today = 6
}
type DefaultDateRange = DateRangeFilterEnum | { startDate: Date; endDate: Date };

export interface DateRangeFilterProps {
	datePickerProps?: DateRangeSelectorProps;
	defaultDateRange?: DefaultDateRange;
	dateFilters?: {
		label: string;
		endDate: number | null;
		startDate: number | null;
		enumValue: DateRangeFilterEnum | null;
	}[];
	hideCustomDates?: boolean;
	onChange?: (startDate?: Date, endDate?: Date, enumValue?: DateRangeFilterEnum) => void;
	/**
	 * Should the tab overflow be hidden
	 */
	hideTabOverflow?: boolean;
}

interface DateRangeFilterState {
	isPickerShown: boolean;
	activeKey: string;
}

const DATE_PICKER_KEY = 'date-picker';

const filters: {
	label: string;
	endDate: number | null;
	startDate: number | null;
	enumValue: DateRangeFilterEnum | null;
}[] = [
		{
			label: 'All-Time',
			endDate: null,
			startDate: null,
			enumValue: DateRangeFilterEnum.AllTime,
		},
		{
			label: 'Last 14 Days',
			endDate: null,
			startDate: -14,
			enumValue: DateRangeFilterEnum.Last14,
		},
		{
			label: 'Last 7 Days',
			endDate: null,
			startDate: -7,
			enumValue: DateRangeFilterEnum.Last7,
		},
		{
			label: 'Today',
			endDate: null,
			startDate: 0,
			enumValue: DateRangeFilterEnum.Today,
		},
	];

class DateRangeFilter extends React.PureComponent<DateRangeFilterProps, DateRangeFilterState> {
	/**
	 * Should the tab overflow be hidden
	 */
	private readonly hideTabOverflow: boolean;

	constructor(props: DateRangeFilterProps) {
		super(props);
		// TODO hook up default date range for date picker
		const defaultTab =
			props.defaultDateRange === undefined
				? `${DateRangeFilterEnum.Last7}`
				: typeof props.defaultDateRange === 'number'
					? `${props.defaultDateRange}`
					: `${(this.props.dateFilters ? this.props.dateFilters : filters).length}`;
		this.state = {
			isPickerShown: false,
			activeKey: defaultTab,
		};
		this.hideTabOverflow = props.hideTabOverflow ?? false;
	}

	handleTabChange = (tabKey: string) => {
		this.setState({ activeKey: tabKey, isPickerShown: (tabKey === DATE_PICKER_KEY) });

		const index = (this.props.dateFilters ? this.props.dateFilters : filters).findIndex(
			(filter) => filter.enumValue === parseInt(tabKey)
		);
		const filter = (this.props.dateFilters ? this.props.dateFilters : filters)[index];

		if (filter) {
			const dateEnum = filter.enumValue ? filter.enumValue : DateRangeFilterEnum.AllTime;
			const startDate = filter.startDate
				? getStartOfDay(Math.abs(filter.startDate))
				: getStartOfDay(0);
			const endDate = filter.endDate
				? moment(startDate).add(filter.endDate, 'd').endOf('d')
				: moment().endOf('d');
			this.props.onChange?.(new Date(startDate), endDate.toDate(), dateEnum);
		}
	};

	render() {
		const cols = (this.props.dateFilters ? this.props.dateFilters : filters).map((f) => (
			<Tabs.TabPane key={`${f.enumValue}`} tab={f.label} />
		));
		const datePicker = !this.state.isPickerShown ? (
			<Button type="primary">
				<CalendarOutlined style={{ marginRight: '0px' }} />
			</Button>
		) : (
			<DateRangeSelector
				{...this.props.datePickerProps}
				onChange={this.props.onChange}
				disabledDate={disableFutureDates}
			/>
		);

		return (
			<Row align="middle" justify="space-between" gutter={32}>
				<Col className={(this.hideTabOverflow) ? styles.tabsWithHiddenDropdown : undefined}>
					<Tabs activeKey={this.state.activeKey} onChange={this.handleTabChange} hideAdd>
						{cols}
						{!this.props.hideCustomDates && (
							<Tabs.TabPane key={DATE_PICKER_KEY} tab={datePicker} />
						)}
					</Tabs>
				</Col>
			</Row>
		);
	}
}

export default DateRangeFilter;
